import { useEffect, useState } from 'react'
import { problemText } from './problems'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { api } from './api'
import Boundary from './Boundary.jsx'
import Button from './Button.jsx'
import ConfirmDialog from './ConfirmDialog.jsx'
import PictureDialog from './PictureDialog.jsx'
import { noteText } from './notes'
import ScheduleDialog from './ScheduleDialog.jsx'
import { FacebookCard, InstagramCard, StoryCard } from './PostPreview.jsx'

/**
 * One post, opened.
 *
 * The list is for deciding which post to look at; this is for looking at it — the preview as
 * Facebook and Instagram will show it, editable in place, with every action it can take at the
 * bottom where a thumb reaches. A queue that shows five posts in full is a queue nobody reads to
 * the end of.
 */
export default function PostModal({
  post,
  business,
  timezoneId,
  canManage,
  busy,
  onClose,
  onChanged,
  onReplaced,
  onAskDesigner,
}) {
  const { t } = useTranslation()

  // Deleting and scheduling ask before they act, and the asking belongs here rather than in each
  // screen that opens a preview. It was in each screen, and the calendar's copy did nothing at all
  // — its delete button closed the sheet and went home. One place, one behaviour.
  const [asking, setAsking] = useState(null)

  // Everything about the picture, behind one short button. Four long labels in the footer wrapped
  // it to four rows and left the previews a strip.
  const [choosingPicture, setChoosingPicture] = useState(false)


  // What went wrong, said here. Every action in this sheet talks to the server, and a refusal that
  // is thrown away looks exactly like a button that does nothing — which is how a published post
  // refusing to be deleted would read.
  const [error, setError] = useState(null)

  // Which action is in flight, by name. A single true/false could only grey everything out; this
  // puts the spinner on the button that was actually pressed.
  const [working, setWorking] = useState(null)

  // A premium drawing is not done when the request comes back — it is queued, and lands a minute
  // later. Held here as the moment it was asked for, so the sheet can watch for a picture newer
  // than that and stop watching when one arrives.
  const [drawing, setDrawing] = useState(null)

  // What to change about the words, and which part of them. Typed here rather than said in the
  // chat because this is where the owner is already reading the caption.
  const [instruction, setInstruction] = useState('')
  const [scope, setScope] = useState('all')

  // What a designer would have mentioned while handing the post over. Asked for when the sheet
  // opens rather than with the post itself: it reads the drawn pixels, and doing that for every
  // post in a list nobody is looking at that closely is work for nothing.
  const [checks, setChecks] = useState([])

  // An element a finding pointed at, and a number that changes on every tap so that tapping the
  // same one twice picks it up again rather than doing nothing the second time.
  const [pointedAt, setPointedAt] = useState(null)

  // Escape closes it, and the page behind does not scroll while it is open — while it is *open*,
  // which is the part that was missing: the sheet is mounted whether or not there is a post to
  // show, so the lock was taken as soon as the screen appeared and given back only on unmount,
  // which never happens. The page could not scroll, and nothing said why.
  useEffect(() => {
    let dropped = false

    setChecks([])

    if (post?.id) {
      // A check that fails is not worth a word to the owner: there is simply nothing to say, which
      // is what an empty list already means.
      api.checkPost(post.id).then(
        (found) => { if (!dropped) setChecks(Array.isArray(found) ? found : []) },
        () => {},
      )
    }

    return () => { dropped = true }
  }, [post?.id, post?.updatedAt])

  useEffect(() => {
    if (!post) return undefined

    function onKey(event) {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [post, onClose])

  // Asked for and not yet here. The sheet asks the server for this post every few seconds until
  // its picture is newer than the moment it was asked for — and gives up out loud after three
  // minutes rather than turning forever, which is the failure this whole change exists to end.
  useEffect(() => {
    if (!drawing) return undefined

    const moved = post?.updatedAt && new Date(post.updatedAt).getTime() > drawing.since
    const landed =
      drawing.want === 'video'
        ? (post?.premiumVideoBuilds ?? 0) > drawing.before
        : (post?.premiumImageBuilds ?? 0) > drawing.before
    const kind = drawing.want === 'video' ? 'moving' : 'drawing'

    // The thing asked for is here: the counter for it went up. The post's clock is not the
    // signal — it moves when the job starts, a minute before anything has landed.
    if (landed) {
      setDrawing(null)
      return undefined
    }

    // The thing asked for is not coming, and the post says why. Said here, in words, instead of
    // the sheet waiting three minutes over a post that already knew.
    if (moved && post?.imageNote) {
      setDrawing(null)
      setError(t(
        post.imageNote === 'scene-untrue'
          ? 'posts.sceneUntrue'
          : `posts.${kind}${post.imageNote === 'refused' ? 'Refused' : 'Failed'}`,
      ))
      return undefined
    }

    if (Date.now() - drawing.since > 3 * 60 * 1000) {
      setDrawing(null)
      setError(t('posts.drawingSlow'))
      return undefined
    }

    const timer = setTimeout(() => onChanged(), 4000)
    return () => clearTimeout(timer)
  }, [drawing, post?.updatedAt, post?.imageNote, post?.premiumImageBuilds, post?.premiumVideoBuilds, onChanged, t])

  if (!post) return null

  // Something asked for and not yet here — a clip being made, a premium picture being drawn.
  // Every button waits with it: a second thing started on a post that is mid-change lands on
  // top of the first, and "оживеј ја сликата" followed by "одобри" used to be exactly that.
  const waiting = drawing !== null

  // One lock for every button: the parent's own busy, an action in flight here, or a wait for
  // something queued. The button that was pressed shows the ring; all the others hold still.
  // "Одобри" tapped while "оживеј ја сликата" was still answering was two changes to one post.
  const locked = busy || waiting || working !== null

  const revisionsLeft = post.textRevisionsLeft ?? 0

  const editable = post.status === 'Draft'

  // What each button is allowed to do, said once. A button that is shown when it cannot work is
  // worse than a missing one: it teaches the owner that clicking here does nothing.
  const gone = post.status === 'Published' || post.status === 'Publishing'
  const hasPicture = post.variants.some((variant) => variant.imageUrl)
  // A photograph under the words, as opposed to a card of words: what the clip button needs. The
  // 3D button that stood beside it is gone — a parallax drift from one photograph is what every
  // phone does for nothing, and nobody spends a credit on it; the drift lives on as a motion a
  // video can be asked for in the conversation, not as a thing to buy.
  const hasPhotograph = Boolean(post.hasPhotograph)
  const drawn = post.imageIsAiGenerated
  const ownPhoto = Boolean(post.products?.[0]?.imageUrl)

  /** Runs one action, says what happened, and leaves the sheet open unless the post is gone. */
  const act = async (action, { close = false, as = 'busy' } = {}) => {
    setAsking(null)
    setError(null)
    setWorking(as)

    try {
      await action()
      onChanged()
      if (close) onClose()
    } catch (failure) {
      setError(problemText(t, failure))
    } finally {
      setWorking(null)
    }
  }

  // The questions are siblings of the sheet, not children of it. A portal's clicks travel up the
  // React tree wherever the DOM puts them, so a dialog nested inside would close the preview behind
  // it on any click that missed the box.
  // Whichever of the four apply to this post, in the order a person would look for them: their own
  // photograph first, then what to draw, then the way back.
  const pictureOptions = gone
    ? []
    : [
        post.products?.[0] && {
          key: 'upload',
          label: t('posts.uploadPhoto'),
          file: true,
          disabled: locked,
          onPick: (file) =>
            act(async () => {
              await api.uploadProductImage(post.products[0].id, file)
              // Uploading a photograph is how someone says "use this one", so it also undoes any
              // earlier "draw me something instead" — and the post is rebuilt at once, because a
              // picture that changes only after a reload looks like nothing happened.
              await api.buildImage(post.id, false)
            }, { as: 'upload' }),
        },

        // No picture yet: build one the ordinary way — their photograph if there is one.
        !hasPicture && {
          key: 'build',
          label: t('posts.buildImage'),
          disabled: locked,
          onPick: () => act(() => api.buildImage(post.id), { as: 'image' }),
        },

        // Drawing again only appears when it would change something. On a post showing the shop's
        // own photograph it re-rendered the same photograph under the same template, which is why
        // it read as a dead button.
        hasPicture && drawn && {
          key: 'again',
          label: t('posts.aiImageAgain'),
          disabled: locked,
          onPick: () => act(() => api.buildImage(post.id, true), { as: 'image' }),
        },

        // One generated clip in place of the slideshow: the picture actually moves, and it brings
        // its own sound, which our own renderer cannot give a reel. Sent the photograph, never the
        // render, so it too needs one.
        hasPhotograph && {
          key: 'move',
          label: t('posts.makeItMove'),
          disabled: locked,
          onPick: () =>
            act(
              async () => {
                await api.makeItMove(post.id)
                setDrawing({ since: Date.now(), want: 'video', before: post.premiumVideoBuilds ?? 0 })
              },
              { as: 'image' },
            ),
        },

        // The premium drawing, always beside the ordinary one and always wearing its price — the
        // choice is a button, and the price is on the button, so nobody discovers it on a bill.
        // For a set the premium drawing is the room: the products together in one place, checked
        // afterwards by other eyes, and not offered when the room changed one of them.
        (!hasPicture || drawn || post.set) && {
          key: 'premium',
          label: t(post.set ? 'posts.setScene' : 'posts.aiImagePremium'),
          disabled: locked,
          onPick: () =>
            act(
              async () => {
                // Queued, not drawn: the answer comes back at once and the picture a minute later.
                await api.buildImage(post.id, true, true)
                setDrawing({ since: Date.now(), want: 'image', before: post.premiumImageBuilds ?? 0 })
              },
              { as: 'image' },
            ),
        },

        // "This photo is bad, draw me something." Only offered when there is a photograph to
        // replace — otherwise drawing is already what happens. Not for a set, whose picture is
        // its page: the only drawing a set takes is the premium room above.
        ownPhoto && !drawn && !post.set && {
          key: 'draw',
          label: t('posts.aiImage'),
          disabled: locked,
          onPick: () => act(() => api.buildImage(post.id, true), { as: 'image' }),
        },

        // And back again.
        ownPhoto && drawn && {
          key: 'own',
          label: t('posts.usePhoto'),
          disabled: locked,
          onPick: () => act(() => api.buildImage(post.id, false), { as: 'image' }),
        },
      ].filter(Boolean)

  return createPortal(
    <>
    <div className="overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="sheet" onClick={(event) => event.stopPropagation()}>
        <Boundary resetOn={post.id} onClose={onClose}>
        <header className="sheet__head">
          <div>
            {/* A set is named by what it is for, not by its first product; the total sits beside
                it because the total is the point of the page. */}
            <strong>
              {post.set?.seriesLabel && post.set?.seriesOrdinal
                ? `${post.set.seriesLabel} #${post.set.seriesOrdinal} · ${post.set.theme}`
                : post.set?.theme ?? post.products?.[0]?.name ?? `#${post.id}`}
            </strong>
            <div className="muted small">
              {t(`posts.status.${post.status}`)}
              {post.scheduledAt && ` · ${new Date(post.scheduledAt).toLocaleString()}`}
              {post.imageIsAiGenerated && ` · ${t('posts.aiBadge')}`}
              {post.set
                ? ` · ${t('posts.setTotal', { count: post.products?.length ?? 0, total: post.set.totalText })}`
                : post.products?.length > 1 && ` · ${t('posts.products', { count: post.products.length })}`}
            </div>

            {/* Printed across the picture, so it is said here in words rather than left to be read
                off a thumbnail. */}
            {post.headline && <div className="headline">{post.headline}</div>}
          </div>

          <button type="button" className="sheet__close" aria-label={t('common.close')} onClick={onClose}>
            ✕
          </button>
        </header>

        <div className="sheet__body">
          {error && <p className="status status--bad">{error}</p>}

          {/* Said where it can be seen, with a ring that turns: this used to be a line rendered
              outside the sheet, under the overlay, which nobody ever saw. */}
          {drawing && (
            <p className="status status--working" role="status" aria-live="polite">
              <span className="spinner spinner--inline" aria-hidden="true" />
              {t(drawing.want === 'video' ? 'posts.movingNote' : 'posts.drawingPremium')}
            </p>
          )}
          {post.imageWarning && (
            <p className="status status--bad">{t('posts.weakImage', { reason: post.imageWarning })}</p>
          )}
          {/* The publisher sent this set back: its sum is not the catalogue's any more. */}
          {(post.imageNote === 'set-total' || post.imageNote === 'set-stock') && (
            <p className="status status--bad">{t('posts.setStale')}</p>
          )}

          <div className="variants">
            {post.variants.map((variant) => {
              const shared = {
                business,
                variant,
                videoUrl: variant.isStory ? post.storyVideoUrl : post.videoUrl,
                disabled: locked || !editable,
                busy: working !== null,
                onSave: (text) => act(() => api.editVariant(post.id, variant.id, text, variant.hashtags)),

                // Everything that was moved, once, when the words are put down. One drag one
                // request meant a second of waiting between each drag and the next. The face it is
                // for is the one they dragged on, which is the only reading of a finger on a
                // particular picture.
                onMove: editable
                  ? (points) =>
                      act(() => api.designPost(post.id, {
                        ...(points.title ? { titleAt: points.title } : {}),
                        ...(points.price ? { priceAt: points.price } : {}),
                        ...(points.headline ? { headlineAt: points.headline } : {}),
                        target: variant.isStory ? 'story' : variant.network.toLowerCase(),
                      }), { as: 'design' })
                  : undefined,

                // Typed into on the picture itself, where a drawing program would put it. The
                // price is never opened: it is the number from the catalogue, and a picture that
                // says a price the shop does not charge is the one thing this must not be able to
                // make.
                onWrite: editable
                  ? (field, value) => act(() => api.designPost(post.id, { [field]: value }), { as: 'design' })
                  : undefined,

                // How the words are set, from the bar over whatever is held. No target: a name is
                // not heavier on Facebook than on Instagram, any more than it is spelled
                // differently there. Where a thing sits is per face; what it looks like is not.
                onSet: editable
                  ? (element, patch) => act(() => api.designPost(post.id, patch), { as: 'design' })
                  : undefined,

                design: post.design,

                // A finding was tapped: pick that element up, so the frame and its bar are where
                // the eye already is.
                pointedAt,

                textOf: (element) => (element === 'title' ? (post.imageTitle ?? '') : (post.headline ?? '')),
              }

              const card = variant.isStory ? (
                <StoryCard {...shared} />
              ) : variant.network === 'Instagram' ? (
                <InstagramCard {...shared} />
              ) : (
                <FacebookCard {...shared} />
              )

              return (
                <figure key={variant.id} className="variant">
                  <figcaption className="variant__where">
                    {variant.network}
                    {variant.isStory && <span className="muted"> · {t('preview.story')}</span>}
                  </figcaption>
                  {card}
                </figure>
              )
            })}
          </div>

          {/* "Скрати го", "спомни бесплатна достава", "тргни ги емоџињата". The post, its pictures
              and its place on the calendar all stay; only the words asked about change. */}
          {editable && (
            <div className="revise">
              <label className="revise__label" htmlFor="revise-instruction">{t('posts.revise')}</label>

              <div className="revise__scope">
                {['all', 'facebook', 'instagram', 'hashtags'].map((which) => (
                  <button
                    key={which}
                    type="button"
                    className={`chip${scope === which ? ' on' : ''}`}
                    disabled={locked}
                    onClick={() => setScope(which)}
                  >
                    {t(`posts.reviseScope.${which}`)}
                  </button>
                ))}
              </div>

              <div className="revise__row">
                <input
                  id="revise-instruction"
                  className="revise__text"
                  value={instruction}
                  placeholder={t('posts.revisePlaceholder')}
                  disabled={locked || revisionsLeft === 0}
                  onChange={(event) => setInstruction(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && instruction.trim()) {
                      event.preventDefault()
                      act(async () => {
                        await api.revisePost(post.id, scope, instruction.trim())
                        setInstruction('')
                      }, { as: 'revise' })
                    }
                  }}
                />

                <Button
                  type="button"
                  className="button"
                  disabled={locked || !instruction.trim() || revisionsLeft === 0}
                  loading={working === 'revise'}
                  onClick={() =>
                    act(async () => {
                      await api.revisePost(post.id, scope, instruction.trim())
                      setInstruction('')
                    }, { as: 'revise' })
                  }
                >
                  {t('posts.reviseSend')}
                </Button>
              </div>

              {/* Not a second chat — a shortcut to the only one, with this post put on the table.
                  Anything the field above cannot do is said there instead: the picture, the layout,
                  the photographs, taking a change back. */}
              {onAskDesigner && (
                <button
                  type="button"
                  className="link"
                  onClick={() => {
                    onClose()
                    onAskDesigner(post)
                  }}
                >
                  {t('posts.askDesigner')}
                </button>
              )}

              {/* The price, before it is charged rather than at the moment of refusal. */}
              <div className="muted small">
                {revisionsLeft > 0 ? (
                  t('posts.reviseLeft', { count: revisionsLeft })
                ) : (
                  <>
                    {t('posts.reviseNone')}{' '}
                    <button
                      type="button"
                      className="link"
                      disabled={locked}
                      onClick={() => act(() => api.buyRevisions(post.id), { as: 'buy' })}
                    >
                      {t('posts.reviseBuy')}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

        </div>

        {/* Advice, never a gate: everything here can be published as it is. A finding that names
            an element picks that element up on the picture, where the frame and its bar already
            know how to change it. */}
        {checks.length > 0 && (
          <div className="findings">
            <span className="findings__title">{t('posts.beforeApproving')}</span>

            {checks.map((note, index) => {
              const element = note.args?.element ?? note.args?.first ?? null

              return (
                <button
                  key={`${note.code}-${index}`}
                  type="button"
                  className={`findings__row${element ? ' findings__row--goes' : ''}`}
                  disabled={!element}
                  onClick={() => element && setPointedAt({ element, nonce: Date.now() })}
                >
                  {noteText(t, note)}
                </button>
              )
            })}
          </div>
        )}

        <footer className="sheet__actions">
          {/* Nothing here changes a post that is already on somebody's page. It is history now.
              Everything about the picture is behind one button: those four carry the longest
              labels in the portal, they all answer the same question, and in a row they were what
              pushed the previews out of a sheet that exists to show them. */}
          {!gone && pictureOptions.length > 0 && (
            <Button
              type="button"
              className="button button--quiet"
              disabled={locked}
              loading={working === 'image' || working === 'upload' || waiting}
              onClick={() => setChoosingPicture(true)}
            >
              {t('posts.picture')}
            </Button>
          )}

          {/* Anything may be tried because anything may be taken back. Hidden when there is
              nothing to go back to: a button that cannot work teaches that buttons do not work. */}
          {!gone && post.revisions > 0 && (
            <Button
              type="button"
              className="button button--quiet"
              disabled={locked}
              loading={working === 'undo'}
              onClick={() => act(() => api.undoPost(post.id), { as: 'undo' })}
            >
              {t('posts.undo', { count: post.revisions })}
            </Button>
          )}

          {!gone && (
            <Button
              type="button"
              className="button button--quiet"
              disabled={locked}
              loading={working === 'rewrite'}
              onClick={() =>
                act(async () => {
                  // Rewriting writes a new post and cancels this one, so the sheet follows it
                  // rather than closing on the owner who just asked for it.
                  const replacement = await api.regeneratePost(post.id)

                  if (replacement?.id && replacement.id !== post.id) onReplaced?.(replacement.id)
                }, { as: 'rewrite' })
              }
            >
              {t('posts.regenerate')}
            </Button>
          )}

          {canManage && post.status === 'Draft' && (
            <Button
              type="button"
              className="button"
              disabled={locked}
              loading={working === 'approve'}
              onClick={() => act(() => api.approvePost(post.id), { as: 'approve' })}
            >
              {t('posts.approve')}
            </Button>
          )}

          {canManage && (post.status === 'Approved' || post.status === 'Scheduled') && (
            <>
              <Button
                type="button"
                className="button button--quiet"
                disabled={locked}
                loading={working === 'schedule'}
                onClick={() => setAsking('schedule')}
              >
                {post.status === 'Scheduled' ? t('calendar.reschedule') : t('calendar.schedule')}
              </Button>

              <Button
                type="button"
                className="button"
                disabled={locked}
                loading={working === 'publish'}
                onClick={() => act(() => api.publishPost(post.id), { as: 'publish' })}
              >
                {t('posts.publishNow')}
              </Button>
            </>
          )}

          {!gone && (
            <Button
              type="button"
              className="button button--quiet"
              disabled={locked}
              loading={working === 'reject'}
              onClick={() => act(() => api.rejectPost(post.id), { close: true, as: 'reject' })}
            >
              {t('posts.reject')}
            </Button>
          )}

          {/* A published post cannot be deleted — it is on somebody's page. The server refuses it
              too; this is so the refusal is never the way the owner finds out. */}
          {canManage && !gone && (
            <Button
              type="button"
              className="link link--danger"
              disabled={locked}
              loading={working === 'delete'}
              onClick={() => setAsking('delete')}
            >
              {t('common.delete')}
            </Button>
          )}
        </footer>
        </Boundary>
      </div>
    </div>

      <ScheduleDialog
        open={asking === 'schedule'}
        timezoneId={timezoneId}
        onCancel={() => setAsking(null)}
        onConfirm={(scheduledAtLocal) =>
          act(() => api.schedulePost(post.id, scheduledAtLocal), { as: 'schedule' })
        }
      />

      <PictureDialog
        open={choosingPicture}
        options={pictureOptions}
        onClose={() => setChoosingPicture(false)}
      />

      <ConfirmDialog
        open={asking === 'delete'}
        title={t('posts.deleteTitle')}
        body={t('posts.deleteBody')}
        confirmLabel={t('common.delete')}
        destructive
        onCancel={() => setAsking(null)}
        onConfirm={() => act(() => api.deletePost(post.id), { close: true, as: 'delete' })}
      />
    </>,
    document.body,
  )
}
